<template lang="pug">
v-card.pa-6.mb-6.rounded-lg(outlined)
  .text-md.font-semibold.neutral700--text.mb-4 Item information
  .text-sm(v-if="!isLocked") Search items you want to order from your inventory list.&nbsp;
    span.font-semibold.underline.cursor-pointer(@click="handleCreateNewItem")
      span Or create new item
      v-icon.cursor-pointer(size="15" color="neutral700") $yzi-arrow-up-right
  v-form(ref="itemForm")
    v-data-table.mt-sm-4.pb-6(:headers="headers" :items="selectedInventory.data" item-key="id" :items-per-page="pageSize" :page="currentPage" hide-default-footer disable-sort :header-props="{ sortIcon: '$yzi-triangle' }")
      template(v-slot:header.transferableStock="{ header }")
        div.d-flex.align-center.gap-1
          span {{ header.text }}
          yz-tooltip.ml-1(attach)
            template(#tooltip) Available stock at requested location
            yz-icon(size="14" color="neutral500") $yzi-info-circle
      template(v-slot:header.requestedQty="{ header }")
        div.d-flex.align-center.gap-1
          span {{ header.text }}
          yz-tooltip.ml-1.d-flex.align-center(v-if="selectedDocument.id === 'stock-transfer'" attach)
            template(#tooltip) Based on requested location's UOM
            yz-icon.mt-n1.bold-icon(size="14" color="neutral500") $yzi-info-circle
          yz-tooltip.ml-1.d-flex.align-center(v-else attach)
            template(#tooltip) Supplier's packaging unit
            yz-icon.mt-n1.bold-icon(size="14" color="neutral500") $yzi-info-circle
      template(v-slot:item.product="{ item }")
        div.my-2
          div.text-sm.neutral700--text {{ item.product.name }}
          div.text-xs.neutral700--text.no-wrap.mt-1(v-if="selectedDocument.id === 'purchase-order'")
            span UOM: {{ item.product.uom || '-' }}
            span.ml-2 Stock: {{ item.product.available_qty || '-' }}
      template(v-slot:item.uom="{ item }")
        .text-sm.my-2 {{ item.product.uom || '-' }}
      template(v-slot:item.transferableStock="{ item }")
        div.d-flex.align-center.justify-space-between.my-2
          .text-sm.neutral700--text {{ item.product.available_qty || 0 }}
          yz-tooltip(attach)
            template(#tooltip)
              div On hand: {{ item.product.inventory_qty || 0 }}
              div Available: {{ item.product.available_qty || 0 }}
              div Reserved: {{ item.product.reserved_qty || 0 }}
              div Committed: {{ item.product.committed_qty || 0 }}
              div Incoming: {{ item.product.preorder_qty || 0 }}
              div To order: {{ item.product.to_order_qty || 0 }}
            yz-icon(size="14" color="neutral500") $yzi-info-circle
      template(v-slot:item.product.available_qty="{ item }")
        div.d-flex.align-center.justify-space-between.my-2
          .text-sm.neutral700--text {{ item.product.available_qty || 0 }}
          yz-tooltip(attach)
            template(#tooltip)
              div On hand: {{ item.product.inventory_qty || 0 }}
              div Available: {{ item.product.available_qty || 0 }}
              div Reserved: {{ item.product.reserved_qty || 0 }}
              div Committed: {{ item.product.committed_qty || 0 }}
              div Incoming: {{ item.product.preorder_qty || 0 }}
              div To order: {{ item.product.to_order_qty || 0 }}
            yz-icon(size="14" color="neutral500") $yzi-info-circle
      template(v-slot:item.requestedQty="{ item }")
        div.d-flex.align-center.my-2(v-if="isLocked")
          .text-sm {{ item.requestedQty }}
          .text-sm.neutral500--text.ml-2(v-if="item.product.selected_uom || item.product.uom") {{ (item.product.selected_uom || item.product.uom) | pluralize({count: item.requestedQty}) }}
        div.d-flex.align-center.my-2(v-else)
          yz-text-field(v-model.number="item.requestedQty" type="number" size="lg" :rules="rules.required_greater_than_zero" width="80px" @input="handleQtyChanged($event, item)")
          yz-dropdown.ml-2(v-if="item.product.uom_conversion_enabled && item.product.uom_conversions && item.product.uom_conversions.length" v-model="item.product.selected_uom" :items="getUomOptions(item.product)" item-text="text" item-value="value" size="lg" width="120px" required)
            template(#item="{ item: uomItem }")
              div.d-flex.align-center.justify-space-between(style="width: 100%;")
                .text-sm.neutral700--text {{ uomItem.text }}
                .text-xs.neutral500--text.ml-4(v-if="uomItem.conversion") {{ uomItem.conversion }}
          .text-sm.neutral500--text.ml-2(v-else-if="item.product.uom") {{ item.product.uom }}
      template(v-slot:item.batchNumber="{ item }")
        div.text-sm.my-2(v-if="isLocked") {{ item.batchNumber }}
        v-text-field.text-sm.my-2.round-xl(v-else v-model="item.batchNumber" outlined dense height="48" hide-details="auto")
      template(v-slot:item.expiryDate="{ item }")
        div.text-sm.my-2(v-if="isLocked")
          span(v-if="item.expiryDate") {{ item.expiryDate | humanizeDate }}
        date-picker.my-2(v-else v-model="item.expiryDate" min-width="200" clearable title="Select expiry date")
      template(v-slot:item.receivedQty="{ item }")
        template(v-if="selectedDocument.id === 'invoice'")
          v-text-field.text-sm.my-2.round-xl(v-if="isDraft || isActive" v-model.number="item.receivedQty" outlined dense height="48" hide-details="auto" type="number" :rules="rules.required_zero_or_positive" @input="handleQtyChanged($event, item)")
          div.text-sm.my-2(v-else) {{ item.receivedQty }}
        template(v-else)
          div.text-sm.my-2(v-if="isLocked") {{ item.receivedQty }}
          v-text-field.text-sm.my-2.round-xl(v-else v-model.number="item.receivedQty" outlined dense height="48" hide-details="auto" type="number" :disabled="status === 'draft' || !parentData" :rules="rules.required_zero_or_positive")
      template(v-slot:item.costPerItem="{ item }")
        div.text-sm.my-2 {{ item.costPerItem | displayPrice }}
      template(v-slot:item.totalCost="{ item }")
        div.text-sm.my-2(v-if="isLocked") {{ item.totalCostInteger | displayPrice }}
        v-text-field.text-sm.my-2.round-xl(v-else v-model.number="item.totalCost" outlined dense height="48" hide-details="auto" type="number" :rules="rules.required_zero_or_positive" :prefix="$currency.symbol" @input="handleTotalCostChanged($event, item)")
      template(v-slot:item.actions="{ item }")
        v-btn.button-xs(v-if="!parentData || status === 'draft'" icon @click="handleRemoveItem(item)")
          v-icon(size="17") $yzi-trash-03
      template(v-slot:no-data)
        v-card.px-4.py-8.border-dashed.mt-4(outlined)
          .d-flex.flex-column.align-center.justify-center
            v-icon.mb-6(size="30") $yzi-colors
            .text-md.font-semibold.mb-1 No items selected
            .text-sm.neutral500--text Any selected items from your list will show up here
    div.d-flex.justify-end(v-if="selectedInventory.totalCount > pageSize")
      yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="selectedInventory.totalCount" attach menu-top @input="pageChanged")
  .d-flex.align-center.mt-2(v-if="!isLocked")
    v-btn.button-sm(outlined @click="openAddItemToListDialog = true") Search from inventory
    .text-sm.font-semibold.mx-4 OR
    v-btn.button-sm(outlined @click="openSelectRecentPurchasesListsDialog = true") Add from your recent purchases/lists
  v-row.mt-6(v-if="selectedDocument.id === 'purchase-order' || selectedDocument.id === 'invoice' || (selectedDocument.id === 'stock-transfer' && ['approved', 'completed'].includes(status))" no-gutters)
    v-spacer
    v-col(cols="3")
      v-row.my-2(no-gutters)
        v-col
          .text-sm {{ $t('subtotal') }}
        v-col.text-right
          .text-sm.no-wrap {{ subtotal | displayPrice }}
      v-divider.my-2
      v-row.my-2(no-gutters)
        v-col
          payment-tax(v-model="tax" :parentData="parentData")
      v-divider.my-2
      v-row.my-2(no-gutters)
        v-col
          payment-adjustment(v-model="paymentAdjustment" :parentData="parentData")
      v-divider.my-2
      v-row.my-2(no-gutters)
        v-col
          delivery-charge(v-model="deliveryCharge" :parentData="parentData")
      v-divider.my-2
      v-row.my-2(no-gutters)
        v-col
          .text-sm {{ $t('total') }}
        v-col.text-right
          .text-sm.no-wrap {{ grandTotal | displayPrice }}
      v-divider.my-2
      v-row.my-2(no-gutters)
        v-col
          .text-sm {{ $t('paid') }}
        v-col.text-right
          .text-sm.no-wrap {{ paidAmount | displayPrice }}
      v-divider.my-2
      v-row.my-2(no-gutters)
        v-col
          .text-sm.font-semibold.no-wrap {{ $t('amount_due') }}
        v-col.text-right
          .text-md.font-semibold.no-wrap {{ dueAmount | displayPrice }}
  .dialogs
    add-item-to-list-dialog(v-if="openAddItemToListDialog" :open.sync="openAddItemToListDialog" :overrideCompanySlug="sourceCompanySlug" :selectedDocument="selectedDocument" @added="handleAddItemToList")
    select-recent-purchases-lists-dialog(v-if="openSelectRecentPurchasesListsDialog" :open.sync="openSelectRecentPurchasesListsDialog" :overrideCompanySlug="sourceCompanySlug" :selectedDocument="selectedDocument" @added="handleAddItemToList")
    add-new-medicine-dialog(v-if="openAddNewMedicineDialog" :open.sync="openAddNewMedicineDialog" @created="openAddNewMedicineDialog = false" @closed="openAddNewMedicineDialog = false")
</template>

<script>
import { mapGetters } from 'vuex'
import AddItemToListDialog from '@/components/clinic/dialogs/purchasehub/AddItemToListDialog.vue'
import DatePicker from '@/components/clinic/custom/DatePicker.vue'
import PaymentTax from '@/components/clinic/purchasehub/document/PaymentTax.vue'
import PaymentAdjustment from '@/components/clinic/purchasehub/document/PaymentAdjustment.vue'
import DeliveryCharge from '@/components/clinic/purchasehub/document/DeliveryCharge.vue'
import SelectRecentPurchasesListsDialog from '@/components/clinic/dialogs/purchasehub/SelectRecentPurchasesListsDialog.vue'
import AddNewMedicineDialog from '@/components/hq/inventory/branches-warehouses/dialog/AddNewMedicineDialog.vue'

export default {
  components: {
    AddItemToListDialog, DatePicker, PaymentTax, PaymentAdjustment, DeliveryCharge, SelectRecentPurchasesListsDialog, AddNewMedicineDialog
  },
  props: {
    selectedDocument: {
      type: Object,
      required: true
    },
    parentData: {
      type: Object,
      default: null
    },
    value: {
      type: Object,
      default: () => ({})
    },
    injectItems: {
      type: Array,
      default: () => ([])
    },
    sourceCompanySlug: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 10,
      showDeleteButton: false,
      openAddItemToListDialog: false,
      openSelectRecentPurchasesListsDialog: false,
      openAddNewMedicineDialog: false,
      updateReceivedQty: {
        openDialog: false,
        selectedItem: null
      },
      selectedInventory: {
        totalCount: 0,
        data: [],
      },
      rules: {
        required_greater_than_zero: [v => !!v || v === 0 || 'This field is required', v => v > 0 || 'Must be greater than 0'],
        required_zero_or_positive: [v => !!v || v === 0 || 'This field is required', v => v >= 0 || 'Cannot be negative'],
      },
      quotation: {
        headers: [
          { text: '#', value: 'no' },
          { text: 'Item', value: 'product', width: '40%' },
          { text: 'Available', value: 'product.available_qty', width: 120 },
          { text: 'Requested', value: 'requestedQty', width: '20%' },
          { text: '', value: 'actions', sortable: false },
        ]
      },
      purchaseOrder: {
        headers: [
          { text: '#', value: 'no' },
          { text: 'Item', value: 'product'},
          { text: 'Batch Number', value: 'batchNumber' },
          { text: 'Expiry Date', value: 'expiryDate' },
          { text: 'Requested', value: 'requestedQty' },
          { text: 'Received', value: 'receivedQty' },
          { text: 'Cost Per Item', value: 'costPerItem' },
          { text: 'Total Cost', value: 'totalCost' },
          { text: '', value: 'actions', sortable: false },
        ]
      },
      invoice: {
        headers: [
          { text: '#', value: 'no' },
          { text: 'Item', value: 'product'},
          { text: 'Batch Number', value: 'batchNumber' },
          { text: 'Expiry Date', value: 'expiryDate' },
          { text: 'Received', value: 'receivedQty' },
          { text: 'Cost Per Item', value: 'costPerItem' },
          { text: 'Total Cost', value: 'totalCost' },
          { text: '', value: 'actions', sortable: false },
        ]
      },
      stockTransfer: {
        headers: [
          { text: '#', value: 'no' },
          { text: 'Item', value: 'product', width: '30%' },
          { text: 'UOM', value: 'uom' },
          { text: 'Transferable Stock', value: 'transferableStock', width: 200 },
          { text: 'Available', value: 'product.available_qty', width: 120 },
          { text: 'Requested', value: 'requestedQty', width: '20%' },
          { text: '', value: 'actions', sortable: false },
        ],
        approvedHeaders: [
          { text: '#', value: 'no' },
          { text: 'Item', value: 'product'},
          { text: 'Batch No.', value: 'batchNumber' },
          { text: 'Expiry Date', value: 'expiryDate' },
          { text: 'Requested', value: 'requestedQty' },
          { text: 'Received', value: 'receivedQty' },
          { text: 'Unit Price', value: 'costPerItem' },
          { text: 'Total Price', value: 'totalCost' },
          { text: '', value: 'actions', sortable: false },
        ]
      },
      tax: 0,
      paymentAdjustment: 0,
      deliveryCharge: 0,
    }
  },
  mounted () {
    if (this.parentData) {
      this.selectedInventory.data = this.parentData.items.map((item, idx) => this.sanitizeData(item, idx, true))
      this.selectedInventory.totalCount = this.parentData.items.length
      this.paymentAdjustment = this.parentData.adjustment_amount
      this.tax = this.parentData.tax_amount
      this.deliveryCharge = this.parentData.delivery_charge
    } else if (this.injectItems.length > 0) {
      this.handleAddItemToList(this.injectItems)
    }
  },
  watch: {
    sourceCompanySlug (newVal, oldVal) {
      if (oldVal && newVal !== oldVal && this.selectedInventory.data.length) {
        this.refreshItemsStock(newVal)
      }
    },
    'selectedInventory.data': {
      handler (newValue) {
        newValue.forEach(item => {
          if (this.selectedDocument.id === 'invoice') {
            item.requestedQty = item.receivedQty
          }
        })
        this.$emit('input', {
          ...this.value,
          items: newValue,
        })
      },
      deep: true
    },
    tax (newValue) {
      this.$emit('input', {
        ...this.value,
        tax: newValue,
      })
    },
    paymentAdjustment (newValue) {
      this.$emit('input', {
        ...this.value,
        paymentAdjustment: newValue,
      })
    },
    deliveryCharge (newValue) {
      this.$emit('input', {
        ...this.value,
        deliveryCharge: newValue,
      })
    },
    dueAmount (newValue) {
      this.$emit('input', {
        ...this.value,
        dueAmount: newValue,
      })
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      isHq: 'hq/getIsHq'
    }),
    headers () {
      switch (this.selectedDocument.id) {
        case 'quotation':
          return this.quotation.headers

        case 'stock-transfer':
          if (['approved', 'completed'].includes(this.status)) {
            return this.stockTransfer.approvedHeaders
          }
          return this.stockTransfer.headers

        case 'purchase-order':
          return this.purchaseOrder.headers

        case 'invoice':
          return this.invoice.headers

        default:
          return []
      }
    },
    status () {
      if (!this.parentData) return 'draft'

      return this.parentData?.request_status || this.parentData?.order_status
    },
    isDraft () {
      return this.status === 'draft'
    },
    isArchived () {
      return this.status === 'archived'
    },
    isCompleted () {
      return this.status === 'completed'
    },
    isActive () {
      return this.status === 'active'
    },
    isCreated () {
      return this.status === 'created'
    },
    isRequested () {
      return this.status === 'requested'
    },
    isRejected () {
      return this.status === 'rejected'
    },
    isLocked () {
      if (this.isArchived || this.isCompleted || this.isCreated) return true
      if (this.selectedDocument.id === 'stock-transfer' && (this.isRequested || this.isRejected)) return true
      return false
    },
    subtotal () {
      return this.selectedInventory.data.reduce((acc, item) => {
        return acc + item.totalCostInteger
      }, 0)
    },
    grandTotal () {
      return this.subtotal + this.tax + this.paymentAdjustment + this.deliveryCharge
    },
    paidAmount () {
      return this.parentData?.paid_amount || 0
    },
    dueAmount () {
      if (this.selectedDocument.id === 'quotation') return 0
      if (this.selectedDocument.id === 'stock-transfer' && !['approved', 'completed'].includes(this.status)) return 0
      return this.grandTotal - this.paidAmount
    },
  },
  methods: {
    validate () {
      return this.$refs.itemForm ? this.$refs.itemForm.validate() : true
    },
    clearItems () {
      this.selectedInventory.data = []
      this.selectedInventory.totalCount = 0
    },
    async refreshItemsStock (companySlug) {
      try {
        const { data } = await this.axios.get(`/clinics/${companySlug}/inventories`, { params: { status: 'published', page_size: -1 } })
        const nameMap = {}
        for (const item of data.results) {
          nameMap[item.name.toLowerCase()] = item
        }
        this.selectedInventory.data.forEach(item => {
          const match = nameMap[item.product.name.toLowerCase()]
          if (match) {
            this.$set(item.product, 'id', match.id)
            this.$set(item.product, 'inventory_qty', match.inventory_qty)
            this.$set(item.product, 'available_qty', match.available_qty)
            this.$set(item.product, 'reserved_qty', match.reserved_qty)
            this.$set(item.product, 'committed_qty', match.committed_qty)
            this.$set(item.product, 'preorder_qty', match.preorder_qty)
            this.$set(item.product, 'to_order_qty', match.to_order_qty)
          } else {
            this.$set(item.product, 'inventory_qty', 0)
            this.$set(item.product, 'available_qty', 0)
            this.$set(item.product, 'reserved_qty', 0)
            this.$set(item.product, 'committed_qty', 0)
            this.$set(item.product, 'preorder_qty', 0)
            this.$set(item.product, 'to_order_qty', 0)
          }
        })
      } catch {
        // silently fail - stock shown may be stale
      }
    },
    handleAddItemToList (items) {
      const sanitizedItems = items.map((item, idx) => this.sanitizeData(item, idx))
      this.selectedInventory.data = [...this.selectedInventory.data, ...sanitizedItems]
      this.selectedInventory.totalCount = this.selectedInventory.data.length
      this.openAddItemToListDialog = false
    },
    sanitizeData (item, idx, isMounted = false) {
      switch (this.selectedDocument.id) {
        case 'quotation':
          return isMounted ? this.getQuotationSanitizeMountedData(item, idx) : this.getQuotationSanitizeAddedData(item, idx)

        case 'stock-transfer':
          if (['approved', 'completed'].includes(this.status)) {
            return isMounted ? this.getPurchaseOrderSanitizeMountedData(item, idx) : this.getPurchaseOrderSanitizeAddedData(item, idx, isMounted)
          }
          return isMounted ? this.getQuotationSanitizeMountedData(item, idx) : this.getQuotationSanitizeAddedData(item, idx)

        case 'purchase-order':
          return isMounted ? this.getPurchaseOrderSanitizeMountedData(item, idx) : this.getPurchaseOrderSanitizeAddedData(item, idx, isMounted)

        case 'invoice':
          return isMounted ? this.getInvoiceSanitizeMountedData(item, idx) : this.getInvoiceSanitizeAddedData(item, idx, isMounted)

        default:
          return item
      }
    },
    getQuotationSanitizeAddedData (item, idx) {
      return {
        no: this.selectedInventory.totalCount + idx + 1,
        product: { ...item, selected_uom: item.uom || null },
        requestedQty: item.requested_qty || 1
      }
    },
    getQuotationSanitizeMountedData (item, idx) {
      return {
        ...this.$options.filters.filterDisallowedObjectProperties(item, ['requested_qty']),
        no:idx + 1,
        id: item.id,
        product: { ...item.product, selected_uom: item.product?.selected_uom || item.product?.uom || null },
        requestedQty: item.requested_qty
      }
    },
    getPurchaseOrderSanitizeAddedData (item, idx) {
      return {
        no: this.selectedInventory.totalCount + idx + 1,
        product: { ...item },
        requestedQty: item.requested_qty || 1,
        batchNumber: item.batch_no ?? null,
        expiryDate: item.expiry_date ?? null,
        receivedQty: 0,
        originalReceivedQty: 0,
        costPerItem: 0,
        originalCost: 0,
        totalCost: 0,
        totalCostInteger: 0
      }
    },
    getPurchaseOrderSanitizeMountedData (item, idx) {
      const totalCost = item.total_cost || 0
      return {
        ...this.$options.filters.filterDisallowedObjectProperties(item, ['requested_qty', 'batch_no', 'expiry_date', 'received_qty']),
        no: idx + 1,
        product: { ...item.product },
        id: item.id,
        requestedQty: item.requested_qty,
        batchNumber: item.batch_no ?? null,
        expiryDate: item.expiry_date ?? null,
        receivedQty: item.received_qty ?? 0,
        originalReceivedQty: item.received_qty ?? 0,
        costPerItem: item.cost_per_item ?? 0,
        originalCost: item.cost_per_item ?? 0,
        totalCost: totalCost / this.$currency.multiplier,
        totalCostInteger: totalCost
      }
    },
    getInvoiceSanitizeAddedData (item, idx) {
      return {
        no: this.selectedInventory.totalCount + idx + 1,
        product: { ...item },
        requestedQty: item.requested_qty || 1,
        batchNumber: item.batch_no ?? null,
        expiryDate: item.expiry_date ?? null,
        receivedQty: 0,
        originalReceivedQty: 0,
        costPerItem: 0,
        originalCost: 0,
        totalCost: 0,
        totalCostInteger: 0
      }
    },
    getInvoiceSanitizeMountedData (item, idx) {
      const originalReceivedQty = this.status === 'draft' ? 0 : item.received_qty
      const originalCost = this.status === 'draft' ? 0 : item.cost_per_item
      return {
        ...this.$options.filters.filterDisallowedObjectProperties(item, ['requested_qty', 'batch_no', 'expiry_date', 'received_qty']),
        no: idx + 1,
        product: { ...item.product },
        id: item.id,
        requestedQty: item.requested_qty,
        batchNumber: item.batch_no,
        expiryDate: item.expiry_date,
        receivedQty: item.received_qty,
        originalReceivedQty,
        costPerItem: item.cost_per_item,
        originalCost,
        totalCost: item.total_cost/this.$currency.multiplier,
        totalCostInteger: item.total_cost
      }
    },
    convertToCurrency (value) {
      return value/this.$currency.multiplier
    },
    pageChanged (page) {
      this.currentPage = page
    },
    handleCreateNewItem () {
      if (this.isHq) {
        this.openAddNewMedicineDialog = true
      } else {
        window.open('/admin/clinic/medication', '_blank')
      }
    },
    handleRemoveItem (item) {
      this.selectedInventory.data = this.selectedInventory.data.filter(inventory => inventory.no !== item.no).map((item, idx) => ({ ...item, no: idx + 1 }))
      this.selectedInventory.totalCount = this.selectedInventory.data.length
    },
    handleTotalCostChanged (inputTotalCost, item) {
      if (inputTotalCost === '') {
        this.$set(item, 'costPerItem', 0)
        this.$set(item, 'totalCostInteger', null)
        return
      }
      const totalCost = parseInt((inputTotalCost * this.$currency.multiplier).toFixed(0))
      if (item.requestedQty === 0 || item.requestedQty === '') {
        this.$set(item, 'costPerItem', 0)
        this.$set(item, 'totalCostInteger', totalCost)
        return
      }
      const costPerItem = totalCost / item.requestedQty
      this.$set(item, 'costPerItem', costPerItem)
      this.$set(item, 'totalCostInteger', totalCost)
    },
    getUomOptions (product) {
      const conversions = product.uom_conversions || []
      const conversionMap = {}
      conversions.forEach(c => {
        const summary = `1 ${c.uom} = ${c.multiplier || 1} ${c.convert_to || ''}`
        if (c.uom) conversionMap[c.uom] = summary
        if (c.convert_to) conversionMap[c.convert_to] = summary
      })
      const units = []
      const seen = []
      conversions.forEach(c => {
        if (c.uom && !seen.includes(c.uom)) {
          seen.push(c.uom)
          units.push({ text: c.uom, value: c.uom, conversion: conversionMap[c.uom] })
        }
        if (c.convert_to && !seen.includes(c.convert_to)) {
          seen.push(c.convert_to)
          units.push({ text: c.convert_to, value: c.convert_to, conversion: conversionMap[c.convert_to] })
        }
      })
      if (units.length) units[units.length - 1].conversion = 'Base UOM'
      return units
    },
    handleQtyChanged (inputQty, item) {
      const qty = parseInt(inputQty)
      if (qty === 0 || !qty) {
        this.$set(item, 'costPerItem', 0)
        return
      }
      this.$set(item, 'costPerItem', item.totalCostInteger / qty)
    }
  },
}
</script>

<style scoped>
.bold-icon >>> svg path {
  stroke-width: 3;
}
</style>